// web/src/components/TournamentWarningModal.tsx
import React, { useState, useEffect } from 'react';

const API_BASE = (import.meta as any).env?.VITE_API_BASE || "http://localhost:8100";

interface TournamentWarningModalProps {
  isOpen: boolean;
  tournamentName: string;
  scheduledStartTime: string; // ISO string
  tournamentId: number;
  userEntryStatus: string;
  onConfirm: () => void;
  onCancel: () => void;
  onViewTournament: () => void;
  onPlayMatch: () => void;
  onEliminated: () => void;
}

export default function TournamentWarningModal({
  isOpen,
  tournamentName,
  scheduledStartTime,
  tournamentId,
  userEntryStatus,
  onConfirm,
  onCancel,
  onViewTournament,
  onPlayMatch,
  onEliminated,
}: TournamentWarningModalProps) {
  const [secondsRemaining, setSecondsRemaining] = useState<number>(0);
  const [currentEntryStatus, setCurrentEntryStatus] = useState<string>(userEntryStatus);

  // Update countdown timer
  useEffect(() => {
    if (!isOpen || !scheduledStartTime) return;

    const updateCountdown = () => {
      const startTime = new Date(scheduledStartTime).getTime();
      const now = Date.now();
      const diff = Math.max(0, Math.floor((startTime - now) / 1000));
      setSecondsRemaining(diff);
    };

    // Update immediately
    updateCountdown();

    // Update every second
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [isOpen, scheduledStartTime]);

  // Poll tournament status to check if user has been eliminated
  useEffect(() => {
    if (!isOpen || !tournamentId) return;

    const checkEliminationStatus = async () => {
      try {
        const headers: HeadersInit = { "Content-Type": "application/json" };
        try {
          const { getValidGuestToken } = await import('../lib/guest-auth');
          const guestToken = await getValidGuestToken();
          if (guestToken) {
            headers["Authorization"] = `Bearer ${guestToken}`;
          }
        } catch {
          // Continue without token for registered users
        }

        const response = await fetch(`${API_BASE}/tournaments?category=active`, {
          credentials: "include",
          headers,
        });

        if (!response.ok) return;

        const data = await response.json();
        const tournament = (data.tournaments || []).find((t: any) => t.id === tournamentId);

        if (tournament && tournament.user_entry) {
          const entryStatus = tournament.user_entry.status;
          setCurrentEntryStatus(entryStatus);

          // If user is eliminated or disqualified, close the modal
          if (entryStatus === "eliminated" || entryStatus === "disqualified") {
            onEliminated();
          }
        } else {
          // Tournament not found or user entry missing - might be eliminated
          onEliminated();
        }
      } catch (error) {
        console.error('[TournamentWarning] Error checking elimination status:', error);
      }
    };

    // Check immediately
    checkEliminationStatus();

    // Poll every 5 seconds to check if user has been eliminated
    const pollInterval = setInterval(checkEliminationStatus, 5000);

    return () => clearInterval(pollInterval);
  }, [isOpen, tournamentId, onEliminated]);

  // Update current entry status when prop changes
  useEffect(() => {
    setCurrentEntryStatus(userEntryStatus);
  }, [userEntryStatus]);

  if (!isOpen) return null;

  const hasStarted = secondsRemaining === 0;
  const isEliminated = currentEntryStatus === "eliminated" || currentEntryStatus === "disqualified";
  const showPlayMatch = hasStarted && !isEliminated;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
      }}
      onClick={onCancel}
    >
      <div
        style={{
          backgroundColor: '#fffaeb',
          border: '3px solid #8b6542',
          borderRadius: '16px',
          padding: '32px',
          maxWidth: '500px',
          width: '90%',
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ marginTop: 0, marginBottom: '16px', color: '#3e2615', fontSize: '24px' }}>
          {hasStarted ? 'Tournament In Progress' : 'Tournament Starting Soon'}
        </h2>
        <p style={{ color: '#3e2615', fontSize: '16px', lineHeight: '1.6', marginBottom: '24px' }}>
          You're registered for <strong>{tournamentName}</strong>
          {hasStarted 
            ? ', which has already started.'
            : `, which starts in ${secondsRemaining} seconds.`}
        </p>
        {!hasStarted && (
          <p style={{ color: '#3e2615', fontSize: '14px', lineHeight: '1.6', marginBottom: '24px' }}>
            Starting a regular game now may cause you to miss your tournament match.
          </p>
        )}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button
            onClick={onCancel}
            style={{
              padding: '12px 24px',
              backgroundColor: 'transparent',
              border: '2px solid #8b6542',
              borderRadius: '8px',
              color: '#3e2615',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
            }}
          >
            Cancel
          </button>
          {showPlayMatch ? (
            <button
              onClick={onPlayMatch}
              style={{
                padding: '12px 24px',
                backgroundColor: '#8b6542',
                border: '2px solid #8b6542',
                borderRadius: '8px',
                color: '#fffaeb',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
              }}
            >
              Play Match
            </button>
          ) : (
            <button
              onClick={onViewTournament}
              style={{
                padding: '12px 24px',
                backgroundColor: '#8b6542',
                border: '2px solid #8b6542',
                borderRadius: '8px',
                color: '#fffaeb',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
              }}
            >
              View Tournament
            </button>
          )}
          <button
            onClick={onConfirm}
            style={{
              padding: '12px 24px',
              backgroundColor: '#d4a574',
              border: '2px solid #8b6542',
              borderRadius: '8px',
              color: '#3e2615',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
            }}
          >
            Start Game Anyway
          </button>
        </div>
      </div>
    </div>
  );
}

